type PdfDoc = {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  setFont: (font: string, style: string) => void;
  setFontSize: (size: number) => void;
  setTextColor: (r: number, g: number, b: number) => void;
  setDrawColor: (r: number, g: number, b: number) => void;
  setLineWidth: (width: number) => void;
  splitTextToSize: (text: string, width: number) => string[];
  text: (
    text: string | string[],
    x: number,
    y: number,
    options?: { align?: string }
  ) => void;
  line: (x1: number, y1: number, x2: number, y2: number) => void;
  addPage: () => void;
  getNumberOfPages: () => number;
  setPage: (page: number) => void;
  save: (filename: string) => void;
};

type PdfCtor = new (options?: { unit?: string; format?: string }) => PdfDoc;

async function loadJsPdf(): Promise<PdfCtor> {
  // Dynamic ESM import keeps jspdf out of the CSV path and works in Vite
  // (unlike require(), which is unavailable in the browser bundle).
  const mod = await import('jspdf');
  return (mod.default ?? mod.jsPDF ?? mod) as PdfCtor;
}

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('-')) return false;
  return /^\|?[\s:|-]+\|[\s:|-]+$/.test(trimmed);
}

function splitTableRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|')) row = row.slice(0, -1);
  return row.split('|').map((cell) => cell.trim());
}

function walkTableBlocks(
  markdown: string,
  onTable: (start: number, end: number, rows: string[][]) => void,
  onProseLine?: (line: string) => void
): void {
  const lines = markdown.split('\n');
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    const next = lines[index + 1];
    if (next && line.includes('|') && isTableSeparator(next)) {
      const header = splitTableRow(line);
      const rows = [header];
      let bodyIndex = index + 2;
      while (bodyIndex < lines.length) {
        const bodyLine = lines[bodyIndex];
        if (!bodyLine.trim() || !bodyLine.includes('|')) break;
        const row = splitTableRow(bodyLine);
        if (row.length !== header.length) break;
        rows.push(row);
        bodyIndex += 1;
      }
      onTable(index, bodyIndex, rows);
      index = bodyIndex;
      continue;
    }
    onProseLine?.(line);
    index += 1;
  }
}

/** Pull every pipe table out of markdown. Returns rows-of-cells per table. */
export function extractMarkdownTables(markdown: string): string[][][] {
  const tables: string[][][] = [];
  walkTableBlocks(markdown, (_start, _end, rows) => tables.push(rows));
  return tables;
}

function proseOutsideTables(markdown: string): string {
  const kept: string[] = [];
  walkTableBlocks(
    markdown,
    () => undefined,
    (line) => kept.push(line)
  );
  return kept.join('\n').trim();
}

/**
 * True when the result is table-shaped enough that CSV is the right export.
 * Short captions are fine; substantial narrative beside a table → PDF.
 */
export function isTableFormatResult(markdown: string): boolean {
  if (extractMarkdownTables(markdown).length === 0) return false;
  return proseOutsideTables(markdown).length <= 120;
}

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) return `"${cell.replace(/"/g, '""')}"`;
  return cell;
}

export function tablesToCsv(tables: string[][][]): string {
  return tables
    .map((table) => table.map((row) => row.map(csvEscape).join(',')).join('\n'))
    .join('\n\n');
}

function sanitizeFilename(name: string): string {
  const cleaned = name
    .replace(/[^\w\s-]+/g, '')
    .trim()
    .replace(/\s+/g, '-');
  return cleaned || 'async-task-results';
}

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function downloadCsv(markdown: string, filename: string): void {
  const blob = new Blob([tablesToCsv(extractMarkdownTables(markdown))], {
    type: 'text/csv;charset=utf-8',
  });
  triggerBrowserDownload(blob, filename);
}

function stripInlineMarkdown(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2');
}

type PdfLine =
  | { kind: 'blank' }
  | { kind: 'heading'; text: string; size: number }
  | { kind: 'list'; text: string; depth: number }
  | { kind: 'paragraph'; text: string };

export function parsePdfLines(markdown: string): PdfLine[] {
  const out: PdfLine[] = [];
  for (const raw of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) {
      if (out.length && out[out.length - 1].kind !== 'blank') {
        out.push({ kind: 'blank' });
      }
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line.trim());
    if (heading) {
      let size = 11;
      if (heading[1].length === 1) size = 14;
      else if (heading[1].length === 2) size = 12;
      out.push({
        kind: 'heading',
        text: stripInlineMarkdown(heading[2]),
        size,
      });
      continue;
    }
    const boldTitle = /^\*\*(.+)\*\*$/.exec(line.trim());
    if (boldTitle && boldTitle[1].length < 80 && !boldTitle[1].includes('**')) {
      out.push({ kind: 'heading', text: boldTitle[1], size: 11 });
      continue;
    }
    const list = /^( *)([-*]|\d+\.)\s+(.*)$/.exec(line);
    if (list) {
      out.push({
        kind: 'list',
        depth: Math.floor(list[1].length / 2),
        text: stripInlineMarkdown(list[3]),
      });
      continue;
    }
    out.push({ kind: 'paragraph', text: stripInlineMarkdown(line.trim()) });
  }
  while (out.length && out[out.length - 1].kind === 'blank') out.pop();
  return out;
}

async function downloadPdf(
  markdown: string,
  filename: string,
  taskName?: string
): Promise<void> {
  const JsPDF = await loadJsPdf();
  const doc = new JsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 18;
  const marginBottom = 20;
  const contentWidth = pageWidth - marginX * 2;
  let y = 18;
  let sawItem = false;

  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - marginBottom) return;
    doc.addPage();
    y = 18;
  };

  if (taskName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    for (const line of doc.splitTextToSize(taskName, contentWidth)) {
      ensureSpace(8);
      doc.text(line, marginX, y);
      y += 7;
    }
    y += 2;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 8;
  }

  for (const block of parsePdfLines(markdown)) {
    if (block.kind === 'blank') {
      y += 3;
      continue;
    }
    if (block.kind === 'heading') {
      if (/^Item\s+\d+/i.test(block.text) && sawItem) {
        y += 4;
        ensureSpace(6);
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.25);
        doc.line(marginX, y, pageWidth - marginX, y);
        y += 4;
      }
      if (/^Item\s+\d+/i.test(block.text)) sawItem = true;
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(block.size);
      const lineH = block.size * 0.45;
      const wrapped = doc.splitTextToSize(block.text, contentWidth);
      ensureSpace(wrapped.length * lineH + 2);
      for (const line of wrapped) {
        doc.text(line, marginX, y);
        y += lineH;
      }
      y += 2;
      continue;
    }
    if (block.kind === 'list') {
      const indent = Math.min(block.depth, 4) * 5;
      const textX = marginX + indent + 5;
      const wrapped = doc.splitTextToSize(
        block.text,
        contentWidth - indent - 5
      );
      ensureSpace(wrapped.length * 5.2 + 1);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('•', marginX + indent, y);
      for (const line of wrapped) {
        doc.text(line, textX, y);
        y += 5.2;
      }
      y += 0.8;
      continue;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const wrapped = doc.splitTextToSize(block.text, contentWidth);
    ensureSpace(wrapped.length * 5.2 + 1);
    for (const line of wrapped) {
      doc.text(line, marginX, y);
      y += 5.2;
    }
    y += 1.2;
  }

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${page} of ${pages}`, pageWidth / 2, pageHeight - 10, {
      align: 'center',
    });
  }
  doc.setTextColor(0, 0, 0);
  doc.save(filename);
}

/** Download combined async-task result: tables → CSV, otherwise → PDF. */
export async function downloadDeliveryResult(
  content: string,
  taskName = 'async-task-results'
): Promise<void> {
  const base = sanitizeFilename(taskName);
  if (isTableFormatResult(content)) {
    downloadCsv(content, `${base}.csv`);
    return;
  }
  await downloadPdf(content, `${base}.pdf`, taskName);
}
